import { Add, Close, Delete } from '@mui/icons-material';
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { DeleteModal } from 'components/Dialog';
import useStateManager from 'components/hooks/useStateManager';
import Loading from 'components/Loading';
import { has, map } from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { resource_types } from 'store/extraResource/constants';
import { handleCreateExtraResource, handleDeleteExtraResource } from 'store/extraResource/thunks';
import styled from 'styled-components';
import { dropdownForResourceType } from 'utils/config/reference';
import ResourceTypeSelector from './resourceTypeSelector';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { extensionActivityIcon } from '../helper';

const initalState = [
  {
    resource_type: resource_types.resource,
    config: {
      title: '',
      selectedItems: [],
    },
  },
];

const CreateExtensionActivites = ({ data, pageId, bookId, open, close }) => {
  const showAlert = useAlert();
  const { AP, story, resource } = resource_types;
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const [states, updateState] = useStateManager({
    activities: initalState,

    //loading
    loading: { type: '', value: false },

    //for delete item
    deleteModal: false,
    deleteLoader: false,
    selectedItem: null,
  });

  useEffect(() => {
    if (data?.length > 0) {
      updateState({ activities: data });
    } else {
      updateState({ activities: initalState });
    }
  }, [data]);

  const updateActivityFields = (index, fieldsToUpdate) => {
    const updatedActivities = [...states.activities];

    // Update the activity at the specified index with new fields
    updatedActivities[index] = {
      ...updatedActivities[index],
      ...fieldsToUpdate,
    };

    updateState({ activities: updatedActivities });
  };

  const onAddActivity = () => {
    try {
      const payload = {
        resource_type: '1wayResource',
        config: {
          title: '',
          selectedItems: [],
        },
      };

      updateState({
        activities: [...states.activities, payload],
      });

      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTo(0, scrollRef.current.scrollHeight);
        }
      }, 500);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const onDelete = async () => {
    updateState({ deleteLoader: true });
    await dispatch(handleDeleteExtraResource(states?.selectedItem?.id));
    updateState({
      deleteLoader: false,
      deleteModal: false,
    });
    if (states?.activities?.length === 1) {
      updateState({
        activities: initalState,
      });
    }
    showAlert('Extension Activity Deleted Successfully', 'success');
  };

  const openDeleteModal = (item) => {
    updateState({
      deleteModal: true,
      selectedItem: item,
    });
  };

  const onRemove = (index) => {
    if (states?.activities?.length === 1) {
      showAlert('At least one activity is required.', 'error');
      return;
    }
    const updatedActivities = [...states.activities];
    const filteredActivite = updatedActivities.filter((_, i) => i !== index);
    updateState({ activities: filteredActivite });
  };

  const isValid = useMemo(() => {
    const isValid = states?.activities?.every((activity) => {
      return (
        activity?.resource_type?.length > 0 &&
        activity?.config?.title?.length > 0 &&
        activity?.config?.selectedItems?.length > 0
      );
    });
    return isValid;
  }, [states?.activities]);

  const handleUpdate = async () => {
    if (!isValid) {
      showAlert("Please Fill all required Field's", 'error');
      return;
    }
    updateState({ loading: { value: true, type: 'update' } });
    try {
      const updatedData = states.activities?.map((element) => {
        return { ...element, page_id: pageId, bookId: bookId };
      });
      await dispatch(handleCreateExtraResource({ data: updatedData }));
      updateState({ loading: { value: false, type: '' } });
      showAlert('Extension Activites Updated Successfully', 'success');
      close();
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
    }
  };


  return (
    <>
      <DeleteModal
        deleteToggle={states.deleteModal}
        onDelete={onDelete}
        loading={states.deleteLoader}
        setDeleteToggle={() => updateState({ deleteModal: false })}
        message={`Are you sure you want to remove the activity on pageId ${pageId}?`}
      />
      <Modal open={open} onClose={close} style={{ height: '100%', width: '100%' }}>
        <ModalContent style={{ width: '70vw' }}>
          <Row style={{ justifyContent: 'space-between' }}>
            <TitleText>Extension Activites</TitleText>
            <IconButton onClick={close} style={{ padding: 0 }}>
              <Close color="secondary" />
            </IconButton>
          </Row>
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={onAddActivity}>
            Add extension activities
          </Button>

          <ModalConfigArea ref={scrollRef}>
            {states.activities.map((element, i) => (
              <LinkArea key={i}>
                <Row gutter={[24, 24]} style={{ justifyContent: 'flex-end', display: 'flex' }}>
                  <IconButton
                    onClick={() => {
                      if (has(element, 'id')) {
                        openDeleteModal(element);
                      } else {
                        onRemove(i);
                      }
                    }}
                    style={{ padding: 0, marginRight: 10 }}
                  >
                    <Delete color="secondary" />
                  </IconButton>
                </Row>

                <Row gutter={[24, 24]} style={{ marginTop: 10, marginRight: 0, marginLeft: 0 }}>
                  <FormControl variant="outlined" style={{ width: '100%' }}>
                    <InputLabel
                      id={`resourceType-${element.id}`}
                      style={{ backgroundColor: '#fff' }}
                    >
                      Resource Type
                    </InputLabel>
                    <Select
                      labelId={`resourceType-${element.id}`}
                      id={`resourceType-${element.id}`}
                      value={element.resource_type || ''}
                      onChange={(e) =>
                        updateActivityFields(i, {
                          resource_type: e.target.value,
                          config: {
                            ...element.config,
                            selectedItems: [],
                          },
                        })
                      }
                    >
                      {map(dropdownForResourceType, (element, i) => (
                        <MenuItem key={i} value={element?.value}>
                          {element?.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Row>

                <Row gutter={[24, 24]} style={{ marginTop: 10, marginRight: 0, marginLeft: 0 }}>
                  <FormControl variant="outlined" style={{ width: '100%' }}>
                    <InputLabel
                      id={`resourceType-${element.id}`}
                      style={{ backgroundColor: '#fff' }}
                    >
                      Type Icon
                    </InputLabel>
                    <Select
                      labelId={`resourceType-${element.id}`}
                      id={`resourceType-${element.id}`}
                      value={element?.config?.key  || ''}
                      onChange={(e) =>
                        updateActivityFields(i, {
                          config: {
                            ...element.config,
                            key: e.target.value,
                          },
                        })
                      }
                    >
                      {map(extensionActivityIcon, (element, i) => (
                        <MenuItem key={i} value={element?.type}>
                          <img src={element?.imageUrl} alt="" className="typeIconExtensionActivitySize" />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Row>

                <div className="mt-10px">
                  <TextField
                    id="displayName"
                    name="displayName"
                    label="Title"
                    InputLabelProps={{
                      shrink: true,
                      required: true,
                    }}
                    style={{ marginBottom: '8px', marginTop: '10px' }}
                    type="text"
                    required
                    variant="outlined"
                    value={element?.config?.title || ''}
                    onChange={(e) =>
                      updateActivityFields(i, {
                        config: {
                          ...element.config,
                          title: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <ResourceTypeSelector
                    data={element?.config?.selectedItems || []}
                    type={element?.resource_type}
                    config={element?.config}
                    handleSubmit={(item, additionalResource) => {
                      console.log(item, 123);
                      updateActivityFields(i, {
                        config: {
                          ...element.config,
                          title: item[0]?.shortName,
                          selectedItems: item,

                          ...(additionalResource &&
                            element?.resource_type === AP && {
                              selectedCategoryAP: additionalResource,
                            }),
                          ...(additionalResource &&
                            element?.resource_type === resource && {
                              linkResource: additionalResource,
                              title: item[0]?.displayName,
                            }),
                          ...(additionalResource &&
                            element?.resource_type === story && {
                              level: additionalResource,
                              title: item[0]?.title,
                            }),
                        },
                      });
                    }}
                    pageId={pageId}
                  />
                </div>
              </LinkArea>
            ))}
          </ModalConfigArea>
          <Row gutter={[24, 24]} style={{ marginTop: 16, marginRight: 0, marginLeft: 0 }}>
            <Button
              fullWidth
              onClick={handleUpdate}
              color="primary"
              variant="contained"
              disabled={states?.loading?.type === 'update' && states?.loading?.value}
            >
              {states?.loading?.type === 'update' && states?.loading?.value ? (
                <Loading white size={14} />
              ) : null}
              {data ? 'Update' : 'Create'}
            </Button>
          </Row>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CreateExtensionActivites;

// Added the missing Row component
const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ModalContent = styled.div`
  left: 50%;
  max-width: 544px;
  min-width: 450px;
  padding: 0px 24px;
  position: absolute;
  min-height: 350px;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: #fff;
  padding: 20px;
  @media only screen and (max-width: 600px) {
    min-width: 90% !important;
  }
`;

const ModalConfigArea = styled.div`
  max-height: 75vh;
  overflow: auto;
  margin-top: 10px;
`;
const LinkArea = styled.div`
  padding: 10px;
  margin: 2px;
  border: 1px dashed;
  margin-top: 24px;
`;
const TitleText = styled.span`
  font-size: 18px;
  color: black;
  font-weight: 600;
`;
